import { TailSpin } from 'react-loader-spinner'
import { Blue300 } from '../../lib/colors'
import { TestItem } from '../../components/TestItem'
import { useCallback, useContext, useRef } from 'react'
import {
  SearchTestFragment,
  useGetAvailableTestsByQueryQuery,
  useGetProfilesSubscription,
} from '../../generated/urql.client'
import { BasketContext, isTestBasketItem } from '../../provider/basket'
import { Link, useSearchParams } from 'react-router-dom'
import { useI18n } from '../../provider/i18n'
import { AuthContext } from '../../provider/auth'
import ProfileSelectorModal, {
  ProfileSelectorModalType,
} from '../../components/ProfileSelectorModal'

export const CatalogSearchPage = () => {
  const [params] = useSearchParams()
  const basket = useContext(BasketContext)
  const auth = useContext(AuthContext)
  const i18n = useI18n()
  const [{ data: testsData, fetching: testsFetching }] =
    useGetAvailableTestsByQueryQuery({
      pause: !basket.address?.location || !params.get('q'),
      variables: {
        point: basket.address?.location || {
          type: 'Point',
          coordinates: [0, 0],
        },
        limit: 1000,
        query: params.get('q') || '',
      },
    })
  const [{ data: profilesData }] = useGetProfilesSubscription({
    pause: !auth.isLogged,
  })
  const profileSelectorModalRef = useRef<ProfileSelectorModalType>(null)

  const onTestAddClick = useCallback(
    (test: SearchTestFragment) => {
      const profilesCount = profilesData?.client_profile.length || 0

      if (!auth.isLogged || profilesCount === 0) {
        basket.addItem({ test })
      } else if (profilesCount === 1) {
        basket.addItem({ test, profile: profilesData?.client_profile[0] })
      } else {
        profileSelectorModalRef.current?.show({
          onChange: (profile) => {
            basket.addItem({ test, profile })
          },
          filter: (profile) =>
            !basket.items.find(
              (item) =>
                isTestBasketItem(item) &&
                item.test.id === test.id &&
                item.profile?.id === profile.id
            ),
        })
      }
    },
    [auth.isLogged, basket, profilesData?.client_profile]
  )

  const tests = testsData?.search_test || []

  return (
    <div className="mb-10">
      <div className="text-blue-700 my-5 flex gap-1 items-center">
        <span>
          <Link to="/">{i18n.t('common.breadcrumbs.mainPage')}</Link>
          {' > '}
          {i18n.t('screens.catalog.search.breadcrumbs.searchResults', {
            query: params.get('q'),
          })}
        </span>
        {testsFetching && <TailSpin color={Blue300} height={16} width={16} />}
      </div>

      {basket.address?.location && !!tests.length && (
        <ul className="md:mx-5 flex flex-col gap-5">
          {tests.map((test) => (
            <li key={test.id}>
              <TestItem
                test={test}
                variant="blue"
                onTestAddClick={() => onTestAddClick(test)}
                showAddButton={
                  auth.isLogged
                    ? basket.items
                        .filter(isTestBasketItem)
                        .filter(({ test: { id } }) => test.id === id).length <
                      (profilesData?.client_profile.length || 0)
                    : !basket.items.find(
                        (item) =>
                          isTestBasketItem(item) && test.id === item.test.id
                      )
                }
              />
            </li>
          ))}
        </ul>
      )}
      <ProfileSelectorModal ref={profileSelectorModalRef} />
    </div>
  )
}
